import { FormBuilder, FormGroup } from "@angular/forms";
import { FormPage } from "./form-page";
import { Condition, IDependencyMap, IToken } from "../../services/form-builder/form.interface";
import { ControlType } from "../enums/ControlType";
import { PageNotFoundError } from "../errors/PageNotFoundError";
import { ControlNotFoundError } from "../errors/ControlNotFoundError";
import { TypeChecker } from "./type-checker";
import { SubscriptionManager } from "./subscription-manager";
import { pairwise, startWith } from "rxjs";
import { BaseControl, Options } from "./form-base-controls";
import { EventEmitter } from "@angular/core";
import { FormNoData } from "../interfaces/database/formularios";
import dayjs from 'dayjs';

type conditionOptions = 'enableIf' | 'visibleIf';

export class DynamicForm {
  private _title: string;
  private _pages: FormPage[];
  private _formGroup: FormGroup;
  private _viewAnswerMode !: boolean;
  private _subForms: Map<string, DynamicForm[]> = new Map<string, DynamicForm[]>();

  private fb: FormBuilder;
  private subscriptionManager: SubscriptionManager = new SubscriptionManager();
  onLoadDataSourceExternal = new EventEmitter<BaseControl<ControlType>>();

  constructor(data: any, viewAnswerMode?: boolean) {
    this.fb = new FormBuilder();
    this._viewAnswerMode = viewAnswerMode ?? false;
    this._formGroup = this.fb.group({ pages: this.fb.group({}) });
    this._title = data.title ?? '';
    this._pages = [];
    if (data.pages)
      this.loadPages(data.pages);
  }

  //#region Getters and Setters
  get title(): string {
    return this._title;
  }

  set title(title: string) {
    this._title = title;

    if (!this._formGroup.contains('title')) {
      this._formGroup.addControl('title', this.fb.control(title));
    } else {
      this._formGroup.get('title')?.setValue(title);
    }
  }

  get pages(): FormPage[] {
    return this._pages;
  }

  get formGroup(): FormGroup {
    return this._formGroup;
  }

  get subForm(): Map<string, DynamicForm[]> {
    return this._subForms;
  }

  //#endregion Getters and Setters

  //#region create/Load Methods
  createNewPage() {
    let newPageName = this.generateUniquePageName();
    //if page is not passed, generate a new page
    const newPage = { name: newPageName, title: newPageName } as FormPage;
    const page = new FormPage(newPage);
    page.createNewControlItem(this.generateUniqueControlName());

    this.pages.push(page);
    (this._formGroup.get("pages") as FormGroup)?.addControl(page.name, page.formGroup);
  }

  private loadPages(pages: any) {
    //Load form from data
    pages.forEach((data: any) => {
      const page = new FormPage(data);

      data.controls.forEach((control: any) => {
        control.disable = this._viewAnswerMode;
        page.loadControl(control);
      });
      page.intializeControlRows();

      this.pages.push(page);
      (this._formGroup.get("pages") as FormGroup)?.addControl(page.name, page.formGroup);
    });

    //if the form is in view answer mode, then theres no need to load any other logic aside from the controls
    if (this._viewAnswerMode) return;

    //setup the dependencies and conditions, this needs to be done after since all controls need to exist
    this.pages.forEach(page => {
      page.controls.forEach(control => {
        //dependencies
        if (TypeChecker.hasOptions(control))
          this.setupControlDependencies(control);
        //conditions
        this.setupControlCondition(control, 'enableIf');
        this.setupControlCondition(control, 'visibleIf');
      });
    });
  }

  createNewControl(index: number, type?: ControlType) {
    const page = this.pages[index];
    if (!page)
      throw new PageNotFoundError(`Page -> at index "${index}", not found. fc:003`);

    const controlName = this.generateUniqueControlName();
    page.createNewControlItem(controlName);
    if (type) {
      this.changeControlType(page.findControl(controlName) as BaseControl<any>, type);
    }
  }
  //#endregion create/Load Methods

  //#region Utility Methods
  private generateUniqueControlName(): string {
    let i = 1;
    let newControlName = 'control';
    //check if the control name already exists
    while (this.pages.find(page => page.controls.find((control: BaseControl<ControlType>) => control.name === newControlName + i) != null) != null) {
      i++;
    }
    return newControlName + i;
  }

  private generateUniquePageName(): string {
    let i = 1;
    let newPageName = 'page';
    //check if the page name already exists
    while (this.pages.find(page => page.name === newPageName + i) != null) {
      i++;
    }
    return newPageName + i
  }

  private findPageIndexByName(name: string): number {
    return this.pages.findIndex(page => page.name === name);
  }

  public findControlIndex(controlName: string): { pageIndex: number, controlIndex: number } | undefined {
    for (let pageIndex = 0; pageIndex < this.pages.length; pageIndex++) {
      let controlIndex = this.pages[pageIndex].findControlIndex(controlName);
      if (controlIndex !== -1) {
        return { pageIndex, controlIndex };
      }
    }
    return undefined;
  }

  private findPageFromGroup(pageName: string): FormGroup {
    const pageGroup = (this._formGroup.get("pages") as FormGroup)?.get(pageName) as FormGroup;
    if (!pageGroup)
      throw new PageNotFoundError(`Page -> "${pageName}", not found. fc:229`);
    return pageGroup;
  }

  private findControlFromGroup(pageName: string, controlName: string): { pageGroup: FormGroup, controlGroup: FormGroup } {
    const pageGroup = this.findPageFromGroup(pageName);
    const controlGroup = pageGroup.get(controlName) as FormGroup;
    if (!controlGroup)
      throw new ControlNotFoundError(`Control -> "${controlName}", not found. fc:236`);
    return { pageGroup, controlGroup };
  }

  findControlFormGroup(controlName: string): FormGroup | null {
    const pagesFormGroup = this._formGroup.get('pages') as FormGroup;
    const pageNames = Object.keys(pagesFormGroup.controls);
    for (let pageName of pageNames) {
      const pageFormGroup = pagesFormGroup.get(pageName) as FormGroup;
      const control = pageFormGroup.get(controlName);
      if (control) {
        return control as FormGroup;
      }
    }
    return null;
  }

  findPageByName(name: string): FormPage | undefined {
    return this.pages.find(page => page.name === name);
  }

  findControlByName(name: string): BaseControl<any> | undefined {
    for (let page of this.pages) {
      let control = page.findControl(name);
      if (control) {
        return control;
      }
    }
    return undefined;
  }

  getControlNamesWithOptions(): string[] {
    return this.pages.flatMap(page => page.controls
      .filter(control => TypeChecker.hasOptions(control))
      .map(control => control.name)
    );
  }

  getControlNames(excludeName: string): string[] {
    return this.pages.flatMap(page => page.controls
      .filter(control => control.name !== excludeName)
      .map(control => control.name)
    );
  }

  //#endregion Utility Methods

  //#region Control Options 
  private unsubscribeDependency(dep: IDependencyMap) {
    if (dep.filterChangedSubId) {
      this.subscriptionManager.removeSubscription(dep.filterChangedSubId);
      dep.filterChangedSubId = undefined;
    }
    if (dep.nameChangedSubId) {
      this.subscriptionManager.removeSubscription(dep.nameChangedSubId);
      dep.nameChangedSubId = undefined;
    }
  }

  private unsubscribeControlDependencies(childControl: Options<any>) {
    //find all the existing dependencies for the control and unsubscribe them
    childControl.dependencyMap.forEach(dep => {
      this.unsubscribeDependency(dep);
    });
  }

  private setupControlDependencies(childControl: Options<any>) {
    this.unsubscribeControlDependencies(childControl);
    //set up the dependencies for the control
    for (let map of childControl.dependencyMap) {
      const parentFormGroup = this.findControlFormGroup(map.control);
      if (!parentFormGroup) throw new ControlNotFoundError(`Form Control -> "${map.control}", not found.`);
      const parentControl = this.findControlByName(map.control);
      if (!parentControl) throw new ControlNotFoundError(`Control -> "${map.control}", not found.`);
      //set up the filter changed subscription
      const filterChanged = parentFormGroup.valueChanges.subscribe(() => {
        this.onValueChanges_UpdateControlOptions(childControl.name);
      });

      map.filterChangedSubId = this.subscriptionManager.addSubscription(filterChanged);
      //set up the name changed subscription
      const nameChanged = parentControl.nameChanged.subscribe((newName: string) => {
        map.control = newName;
      });
      map.nameChangedSubId = this.subscriptionManager.addSubscription(nameChanged);
    }
  }

  private removeControlDependencies(control: Options<any>) {
    this.unsubscribeControlDependencies(control);
    control.dependencyMap = new Set();
    //search all the key values and unsubscribe the control
    this.pages.forEach(p => {
      p.controls.forEach(c => {
        if (TypeChecker.hasOptions(c)) {
          const newDependencies = new Set([...c.dependencyMap].filter(d => {
            if (d.control === control.name) {
              this.unsubscribeDependency(d);
              return false;
            }
            return true;
          }));
          c.dependencyMap = newDependencies;
        }
      });

    });
  }

  private onValueChanges_UpdateControlOptions(childControlName: string) {
    const childControl = this.findControlByName(childControlName);
    if (!childControl) return;
    if (!TypeChecker.hasOptions(childControl)) return;
    childControl.resetFilter();
    if (childControl?.dependencyMap) {
      childControl.dependencyMap.forEach(dependency => {
        const parentFormGroup = this.findControlFormGroup(dependency.control);
        const parentControlValue = parentFormGroup?.value;
        if (parentControlValue && parentControlValue !== '') {
          childControl.filterOptions(dependency.mappedField, parentControlValue);
        }
      });
    }
  }
  //#endregion Control Options

  //#region Conditions

  evaluateConditions(control: BaseControl<any>, conditionName: conditionOptions) {
    const result = this.evaluateCondition(control[conditionName], control.type);
    switch (conditionName) {
      case 'enableIf':
        if (result)
          control.FormControl.enable();
        else
          control.FormControl.disable();
        break;
      case 'visibleIf':
        if (result) {
          control.FormControl.enable();
          control.visible = true;
          control.setValidators();
        }
        else {
          control.visible = false;
          control.FormControl.disable();
          control.FormControl.clearValidators();
        }
        break;
    }
  }

  private evaluateCondition(conditions: IToken[], type: ControlType): boolean {
    if (conditions.length === 0) return false;
    let result = this.evaluateToken(conditions[0], type);
    for (let i = 1; i < conditions.length; i++) {
      const token = conditions[i];
      const tokenValue = this.evaluateToken(token, type);
      switch (token.operator) {
        case 'and':
          result = result && tokenValue;
          break;
        case 'or':
          result = result || tokenValue;
          break;
        default:
          break;
      }
    }
    return result;
  }

  private evaluateToken(token: IToken, type: ControlType): boolean {
    const value = this.findControlFormGroup(token.controlName)?.value;
    switch (token.condition) {
      case Condition.Empty:
        return !value;
      case Condition.NotEmpty:
        return !!value;
      case Condition.Equals:
        return value === token.value;
      case Condition.DoesNotEqual:
        return value !== token.value;
      case Condition.GreaterThan:
        return parseInt(value) > parseInt(token?.value ?? '0');
      case Condition.LessThan:
        return parseInt(value) < parseInt(token?.value ?? '0');
      case Condition.GreaterThanOrEqualsYears: {
        return this.compareYearsFromDate(value, parseInt(token?.value ?? '0'), Condition.GreaterThanOrEqualsYears);
      }
      case Condition.LessThanOrEqualsYears: {
        return this.compareYearsFromDate(value, parseInt(token?.value ?? '0'), Condition.LessThanOrEqualsYears);
      }
      default:
        return false;
    }
  }

  private compareYearsFromDate(dateValue: any, years: number, operator: Condition.GreaterThanOrEqualsYears | Condition.LessThanOrEqualsYears): boolean {
    if (!dateValue) return false;

    const date = dayjs(dateValue);
    if (!date.isValid()) return false;

    const currentDate = dayjs();
    const yearsDifference = currentDate.diff(date, 'year');

    if (operator === Condition.GreaterThanOrEqualsYears) {
      return yearsDifference >= years;
    } else {
      return yearsDifference <= years;
    }
  }

  private unsubscribeFromCondition(token: IToken) {
    if (token.valueChangedsubId) {
      this.subscriptionManager.removeSubscription(token.valueChangedsubId);
      token.valueChangedsubId = undefined;
    }

    if (token.nameChangedSubId) {
      this.subscriptionManager.removeSubscription(token.nameChangedSubId);
      token.nameChangedSubId = undefined;
    }
  }

  private unsubscribeFromConditions(control: BaseControl<any>, conditionName: conditionOptions) {
    control[conditionName].forEach(token => {
      this.unsubscribeFromCondition(token);
    });
  }

  private removeConditionControl(control: BaseControl<any>, conditionName: conditionOptions) {
    this.unsubscribeFromConditions(control, conditionName);
    control[conditionName] = [];
    //search all the key values and unsubscribe the control
    this.pages.forEach(p => {
      p.controls.forEach(c => {
        const newTokens = c[conditionName].filter(t => {
          if (t.controlName === control.name) {
            this.unsubscribeFromCondition(t);
            return false;
          }
          return true;
        });
        c[conditionName] = newTokens;
      });
    });
  }

  private setupControlCondition(control: BaseControl<any>, conditionName: conditionOptions) {
    if (!control[conditionName]) return;
    this.unsubscribeFromConditions(control, conditionName);
    if (control[conditionName].length === 0) {
      control.FormControl.enable();
      control.visible = true;
    }
    control[conditionName].forEach(token => {
      const parentFormGroup = this.findControlFormGroup(token.controlName);
      if (!parentFormGroup) throw new ControlNotFoundError(`Form Control -> "${token.controlName}", not found.`);
      const parentControl = this.findControlByName(token.controlName);
      if (!parentControl) throw new ControlNotFoundError(`Control -> "${token.controlName}", not found.`);

      //set up the value changed subscription
      const subscription = parentFormGroup.valueChanges.pipe(
        startWith(parentFormGroup.value),
        pairwise()
      ).subscribe(([oldValue, newValue]) => {
        if (oldValue === newValue) return;
        this.evaluateConditions(control, conditionName);
      });
      token.valueChangedsubId = this.subscriptionManager.addSubscription(subscription);

      //set up the name changed subscription
      const nameChanged = parentControl.nameChanged.subscribe((newName: string) => {
        token.controlName = newName;
      });
      token.nameChangedSubId = this.subscriptionManager.addSubscription(nameChanged);
      this.evaluateConditions(control, conditionName);

    });
  }

  //#endregion Conditions

  //#region Updates
  updatePageProperties(page: FormPage, newValues: any) {
    if (page.name !== newValues.name) {
      const pagesGroup = (this._formGroup.get("pages") as FormGroup);
      pagesGroup.removeControl(page.name);
      page.name = newValues.name;
      pagesGroup.addControl(newValues.name, page.formGroup);
    }
    page.title = newValues.label;
    page.hideTitle = newValues.hideTitle;
  }

  updateControlProperties(page: FormPage, control: BaseControl<any>, newValues: any): boolean {
    let refreshAfterUpdate = false;
    //changes the control type
    if (control.type !== newValues.type) {
      this.changeControlType(control, newValues.type)
      control = this.findControlByName(control.name) as BaseControl<any>;
      refreshAfterUpdate = true;
    }
    //updates the name of the control      
    if (control.name !== newValues.name) {
      const { pageGroup, controlGroup } = this.findControlFromGroup(page.name, control.name);
      pageGroup.removeControl(control.name);
      control.name = newValues.name;
      pageGroup.addControl(newValues.name, controlGroup);
    }

    //sets the validations
    if (control.isRequired !== newValues.isRequired) {
      control.isRequired = newValues.isRequired;
      control.setValidators();
    }
    //Update everything else that needs it
    control.label = newValues.label;
    if (TypeChecker.hasTooltip(control)) {
      control.tooltip = newValues.tooltip;
    }

    if (TypeChecker.hasPlaceholder(control))
      control.placeholder = newValues.placeholder;

    if (TypeChecker.isInputMask(control))
      control.mask = newValues.mask;

    if (TypeChecker.isCalendar(control)) {
      control.dateFormat = newValues.dateFormat ?? control.dateFormat;
    }
    if (TypeChecker.isFileUpload(control)) {
      control.chooseLabel = newValues.chooseLabel;
      control.accept = newValues.accept;
      control.multipleFiles = newValues.multipleFiles;
    }
    if (TypeChecker.hasRows(control)) {
      control.rows = newValues.rows;
    }
    if (TypeChecker.hasMinMax(control)) {
      control.min = newValues.min;
      control.max = newValues.max;
    }
    if (TypeChecker.isSubFormulario(control)) {
      control.removeAddBtn = newValues.removeAddBtn;
      control.removeDeleteBtn = newValues.removeDeleteBtn;
    }
    if (TypeChecker.hasReadonly(control)) {
      control.readonly = newValues.readonly;
    }
    return refreshAfterUpdate
  }

  updateChoiceOptionProperties(control: BaseControl<any> & Options<any>, newdependencies: IDependencyMap[]) {
    const depenencyValues: Set<IDependencyMap> = new Set();
    //get valid dependencies and make sure they are unique with the set
    newdependencies.filter((item) => item.mappedField && item.control)
      .forEach((item) => {
        const dependencyMap = { mappedField: item.mappedField, control: item.control } as IDependencyMap;
        if (!depenencyValues.has(dependencyMap))
          depenencyValues.add(dependencyMap);
      });

    control.dependencyMap = depenencyValues;
    this.setupControlDependencies(control);
  }

  updateControlLayoutProperties(page: FormPage, control: BaseControl<any>, newValues: any) {
    if (control.startOnNewLine !== newValues.startOnNewLine) {
      control.startOnNewLine = newValues.startOnNewLine;
      page.intializeControlRows();
    }
    control.widthSmallScreen = newValues.widthSmallScreen;
    control.widthMediumScreen = newValues.widthMediumScreen;
    control.widthLargeScreen = newValues.widthLargeScreen;
    control.widthLargerScreen = newValues.widthLargerScreen;

    if (TypeChecker.hasShowHorizontal(control)) {
      control.showHorizontal = newValues.showHorizontal;
    }
  }

  updateControlFontSizeProperty(control: BaseControl<any>, newValue: string) {
    if (TypeChecker.hasFontSize(control)) {
      control.fontSize = newValue;
    }
  }

  updateControlchoiceOptionsProperties(control: BaseControl<any>, newValues: any) {
    if (TypeChecker.hasFilter(control)) {
      control.filter = newValues.addfilter;
    }
    if (TypeChecker.hasMultiSelect(control)) {
      control.multiSelect = newValues.multiSelect;
    }
    if (TypeChecker.hasOptions(control) && TypeChecker.isSourceDatasetDinamico(control)) {
      if (control.dataSource !== newValues.dataSource) {
        control.dataSource = newValues.dataSource;
        control.tablaConsulta = undefined;
        control.columnName = undefined;
        control.columnValue = undefined;
        this.onLoadDataSourceExternal.emit(control);
      }
    }
  }

  updateControlDataSourceConfigTramAutomaticos(control: BaseControl<any>, tablaConsulta: number, columnName: string, columnValue: string) {
    if (TypeChecker.isSourceDatasetDinamico(control)) {
      control.tablaConsulta = tablaConsulta;
      control.columnName = columnName;
      control.columnValue = columnValue;
    }
  }

  updateControlEnableIfConditions(control: BaseControl<any>, conditions: IToken[]) {
    control.enableIf = conditions;
    this.setupControlCondition(control, 'enableIf');
  }

  updateControlVisibleIfConditions(control: BaseControl<any>, conditions: IToken[]) {
    control.visibleIf = conditions;
    this.setupControlCondition(control, 'visibleIf');
  }

  updateControlSubFormularioSelect(control: BaseControl<any>, formulario: FormNoData) {
    if (TypeChecker.isSubFormulario(control)) {
      control.formId = formulario.id;
      control.version = formulario.version;
    }
  }
  //#endregion Updates

  //#region Delete
  deletePage(index: number) {
    const page = this.pages[index];
    if (!page)
      throw new PageNotFoundError(`Page -> at index "${index}", not found`);

    //remove all the dependencies of the controls if there are any
    page.controls.forEach(control => {
      if (TypeChecker.hasOptions(control))
        this.removeControlDependencies(control)
    });
    //delete the control
    this.pages.splice(index, 1);
    (this._formGroup.get("pages") as FormGroup)?.removeControl(page.name);
  }

  deleteControl(pageIndexOrName: number | string, controlIndex: number) {
    let pageIndex = pageIndexOrName;
    if (typeof pageIndexOrName === 'string')
      pageIndex = this.findPageIndexByName(pageIndexOrName);

    pageIndex = pageIndex as number;
    const page = this.pages[pageIndex];
    if (!page)
      throw new PageNotFoundError(`Page -> at index "${pageIndex}", not found`);

    //remove all the posible subscriptions
    const control = page.controls[controlIndex];
    if (!control)
      throw new ControlNotFoundError(`Control -> at index "${controlIndex}", not found`);

    if (TypeChecker.hasOptions(control)) {
      this.removeControlDependencies(control);
    }

    this.removeConditionControl(control, 'enableIf');
    this.removeConditionControl(control, 'visibleIf');

    //delete the control
    page.controls.splice(controlIndex, 1);
    page.formGroup?.removeControl(control.name);
    page.intializeControlRows();
  }

  deleteControlByName(controlName: string) {
    const result = this.findControlIndex(controlName);
    if (!result)
      throw new ControlNotFoundError(`Control -> at index "${controlName}", not found`);
    this.deleteControl(result?.pageIndex, result?.controlIndex);
  }
  //#endregion Delete

  //#region Private Methods
  private changeControlType(control: BaseControl<any>, newType: ControlType) {
    const result = this.findControlIndex(control.name);
    if (!result) return;
    const page = this.pages[result.pageIndex];
    //do this step in case there are subscriptions
    if (TypeChecker.hasOptions(control))
      this.removeControlDependencies(control);

    let updatedControl = page.initializeControlByType(newType, control)

    page.controls[result.controlIndex] = updatedControl
    page.intializeControlRows();
  }
  //#endregion Private Methods

  //#region Public Methods
  toJSON(): any {
    return {
      pages: this._pages.map(page => page.toJSON())
    }
  }

  getFormValues(): any {
    this.subForm.forEach((subform, name) => {
      let control = this.findControlFormGroup(name);
      control?.setValue(subform.map(sf => sf.formGroup.get('pages')?.value ?? {}));
    });
    const pagesFormGroup = this._formGroup.get('pages') as FormGroup;
    return pagesFormGroup.value;
  }

  clearPageValues(pageName: string) {
    const page = this.findPageByName(pageName);
    if (!page) return;
    page.controls.forEach(control => {
      if (control.type === ControlType.SubFormulario) {
        const subform = this.subForm.get(control.name);
        subform?.forEach(f => {
          f.pages.forEach(p => {
            p.controls.forEach(c => {
              c.emitResetEvent();
            });
            p.formGroup.reset();
          });
        });
      }
      control.emitResetEvent();
    });
    page.formGroup.reset();
  }
  //#endregion Public Methods

  //#region SubForms
  addSubForm(name: string, subForms: DynamicForm[]) {
    this._subForms.set(name, subForms);
  }

  removeSubForm(name: string) {
    this._subForms.delete(name);
  }

  //#endregion SubForms
}

